import { compose } from 'redux';
import { useEffect, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useDropzone } from 'react-dropzone';
import { connect, useSelector } from 'react-redux';
import withStyles from '@material-ui/styles/withStyles';
import { BasePage } from 'altus-ui-components';
import {
  getSummarizedDataStateFromState,
  getAvailableFileCategoriesFromState,
  getCurrentUserFromState,
  getAllOrganizationsFromState,
} from 'app/app.selectors';
import {
  EMPTY_SET,
  ProjectPermission,
  MAX_UPLOAD_FILE_SIZE,
} from 'app/app.constants';
import { FIELDS_ACTIONS } from 'features/fields/fields.constants';
import {
  deleteFile,
  downloadFile,
  onLoad,
  onUnload,
  updateFile,
  uploadFiles,
} from 'features/fields/fieldDocuments.actions';
import { setFile } from 'app/app.actions';
import { useHeader } from 'app/hooks/useHeader';
import { Fab } from 'app/components/withTooltip';
import UploadDocumentModal from 'app/components/UploadDocumentModal';
import HasProjectPermission from 'app/components/HasProjectPermission';
import { useProjectPermissions } from 'app/hooks/authorization/useProjectPermissions';
import { getFieldDocumentsFromState } from 'features/fields/field.selectors';
import FieldDocumentsTable from 'features/fields/documents/FieldDocumentsTable';
import { DOCUMENTS_ACTIONS } from 'features/projects/documents/documents.constants';
import { WELLBORE_FILE_ACTIONS } from 'features/wells/wellboreFiles.constants';
import { downloadFile as downloadProjectFile } from 'features/projects/documents/documents.actions';
import { downloadFile as downloadWellboreFile } from 'features/wells/wellboreFiles.actions';
import DocumentsCategoryFilter from 'features/projects/documents/newDocumentsContainer/components/DocumentsCategoryFilter';
import { ALTUS_ORGANIZATION_CODE } from 'app/hooks/useIsInternalCategoryToDisplay/constants';

const FieldDocumentsContainer = ({
  classes,
  dataState,
  fieldId,
  breadcrumb,
  dispatchOnLoad,
  dispatchOnUnload,
  dispatchDropFiles,
  dispatchDeleteFile,
  dispatchUpdateFile,
  dispatchUploadFiles,
  dispatchDownloadFile,
  dispatchDownloadWellboreFile,
  dispatchDownloadProjectFile,
}) => {
  useHeader({ subTitle: breadcrumb });

  const user = useSelector(getCurrentUserFromState);
  const availableOrganizations = useSelector(getAllOrganizationsFromState);

  useEffect(() => {
    dispatchOnLoad(fieldId);

    return () => dispatchOnUnload();
  }, [fieldId, dispatchOnLoad, dispatchOnUnload]);

  const { hasPermission: hasUploadDocumentPermission } = useProjectPermissions(
    ProjectPermission.UPLOAD_DOCUMENTS,
  );

  const [filter, setFilter] = useState(EMPTY_SET);
  const [filterApplied, setFilterApplied] = useState(false);

  const documents = useSelector(getFieldDocumentsFromState);
  const documentCategories = useSelector(getAvailableFileCategoriesFromState);

  const filteredDocuments = useMemo(() => {
    if (filter.isEmpty()) return documents;

    return documents.filter((document) => {
      const documentCategory = documentCategories.get(document.get('category'));

      return filter.includes(documentCategory);
    });
  }, [filter, documents, documentCategories]);

  useEffect(() => {
    if (filteredDocuments?.isEmpty()) {
      setFilter(EMPTY_SET);
    }

    // clear filter if deleting the last document to prevent a filter being active when no documents match it
  }, [filteredDocuments]);

  const downloadFile = useCallback(
    (fieldFileId, file) => dispatchDownloadFile(fieldId, fieldFileId, file),
    [fieldId, dispatchDownloadFile],
  );

  const downloadWellboreFile = useCallback(
    (wellboreId, wellboreFileId, wellboreFile) =>
      dispatchDownloadWellboreFile(
        wellboreId,
        wellboreFileId,
        wellboreFile,
        fieldId,
      ),
    [dispatchDownloadWellboreFile, fieldId],
  );

  const downloadProjectFile = useCallback(
    (projectId, fileId, file) =>
      dispatchDownloadProjectFile(projectId, fileId, file),
    [dispatchDownloadProjectFile],
  );

  const deleteFile = useCallback(
    (fieldFileId) => dispatchDeleteFile(fieldId, fieldFileId),
    [fieldId, dispatchDeleteFile],
  );

  const updateFile = useCallback(
    (file) => dispatchUpdateFile(fieldId, file.fieldFileId, file),
    [fieldId, dispatchUpdateFile],
  );

  const uploadFiles = useCallback(
    (files) => dispatchUploadFiles(fieldId)(files),
    [fieldId, dispatchUploadFiles],
  );

  const onDrop = useCallback(
    (files) => dispatchDropFiles(files),
    [dispatchDropFiles],
  );

  const clearFilters = () => {
    setFilter(EMPTY_SET);
    setFilterApplied(false);
  };

  const { isDragActive, getRootProps, getInputProps, open, fileRejections } =
    useDropzone({
      onDrop,
      noClick: true,
      noKeyboard: true,
      maxSize: MAX_UPLOAD_FILE_SIZE,
      noDrag: !hasUploadDocumentPermission,
    });

  const checkIfUserOrgIsAltus = (user, organizations) => {
    const currentOrg = Array.from(organizations).find(
      (org) => org.get('id') === user.get('organizationId'),
    );

    if (currentOrg?.get('code') === ALTUS_ORGANIZATION_CODE) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <BasePage
      dataState={dataState}
      classes={{
        children: classes.basePageChildren,
        root: classNames({
          [classes.dragOver]: isDragActive,
        }),
      }}
    >
      <Grid item container spacing={4} xs {...getRootProps()}>
        <Grid item xs={2}>
          <DocumentsCategoryFilter
            filter={filter}
            clearFilters={clearFilters}
            setFilter={setFilter}
            documents={documents}
            documentCategories={documentCategories}
            setFilterApplied={setFilterApplied}
            isAltus={checkIfUserOrgIsAltus(user, availableOrganizations)}
          />
        </Grid>
        <Grid item xs={8}>
          <FieldDocumentsTable
            fieldId={fieldId}
            updateFile={updateFile}
            deleteFile={deleteFile}
            downloadFile={downloadFile}
            documents={filteredDocuments}
            documentCategoriesById={documentCategories}
            hasUploadDocumentPermission={hasUploadDocumentPermission}
            noItemsMessage="Upload one or more documents simultaneously by using the + button or drag them here"
            downloadWellboreFile={downloadWellboreFile}
            downloadProjectFile={downloadProjectFile}
            filterApplied={filterApplied}
          />
        </Grid>
        <HasProjectPermission permissions={ProjectPermission.UPLOAD_DOCUMENTS}>
          <UploadDocumentModal
            uploadFiles={uploadFiles}
            enableFileCategories={false}
            rejectedFiles={fileRejections}
            availableFileCategories={documentCategories.valueSeq()}
            dialogContentText="Set meta data and upload files to project"
          />
          <Fab
            onClick={open}
            color="primary"
            title="Upload documents"
            className={classes.create}
          >
            <AddIcon />
          </Fab>
        </HasProjectPermission>
      </Grid>
      <input {...getInputProps()} />
    </BasePage>
  );
};

const mapStateToProps = (state) => ({
  dataState: getSummarizedDataStateFromState(
    state,
    FIELDS_ACTIONS.DOWNLOAD_FIELD_DOCUMENT,
    FIELDS_ACTIONS.UPDATE_FIELD_DOCUMENT,
    FIELDS_ACTIONS.UPDATE_FIELD_DOCUMENT,
    FIELDS_ACTIONS.DELETE_FIELD_DOCUMENT,
    FIELDS_ACTIONS.FIELD_DOCUMENTS_PAGE_LOADED,
    WELLBORE_FILE_ACTIONS.DOWNLOAD_WELLBORE_FILE,
    DOCUMENTS_ACTIONS.DOWNLOAD_PROJECT_FILE,
  ),
});

const mapDispatchToProps = {
  dispatchOnLoad: onLoad,
  dispatchOnUnload: onUnload,
  dispatchDropFiles: setFile,
  dispatchDeleteFile: deleteFile,
  dispatchUpdateFile: updateFile,
  dispatchUploadFiles: uploadFiles,
  dispatchDownloadFile: downloadFile,
  dispatchDownloadWellboreFile: downloadWellboreFile,
  dispatchDownloadProjectFile: downloadProjectFile,
};

const styles = (theme) => ({
  create: {
    zIndex: 2,
    position: 'absolute',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
  },
  dragOver: {
    opacity: '0.3',
    border: '5px gray dashed',
  },
  basePageChildren: {
    flexDirection: 'row',
    paddingTop: theme.spacing(2.25),
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles),
)(FieldDocumentsContainer);
